import { Entity } from '@adornis/baseql/decorators.js';
import { getStammdatenByUserAndInstitution } from '@adornis/buchungsheld-db/api/stammdaten.js';
import type { RegistrationProductWaitinglistPriorityRulesPlugin } from '@adornis/buchungsheld-db/db-plugins.js';
import { ProductWaitinglistPriorityRule } from '@adornis/buchungsheld-db/entities/ProductWaitinglistPriorityRule.js';
import type { BHTranslationDictionary } from '@adornis/buchungsheld-db/translations/translation.js';
import type { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { nothing, type TemplateResult } from 'lit';
import type { IcsrmStammdaten } from '../stammdaten/icsrm-stammdaten-plugin.js';

@Entity()
export class IcsrmImmigrationWaitinglistPriorityRule extends ProductWaitinglistPriorityRule {
  static override _class = 'IcsrmImmigrationWaitinglistPriorityRule';

  override hasValue(): boolean {
    return true;
  }

  override renderEditUI(opt: { tr: TranslationController<BHTranslationDictionary> }) {
    return nothing as unknown as TemplateResult<1>;
  }

  override async hasHighPriority(userID: string): Promise<boolean> {
    const userStammdaten = await getStammdatenByUserAndInstitution<IcsrmStammdaten>(
      userID,
      this.institutionID,
    )({ isImmigrant: 1, nationality: 1, countryOfBirth: 1 });

    if (!userStammdaten)
      throw new Error('could not find stammdaten for user=' + userID + ', institution=' + this.institutionID);

    return !!userStammdaten.isImmigrant || userStammdaten.nationality !== 'DE' || userStammdaten.countryOfBirth !== 'DE';
  }

  override async label() {
    return [
      'Hohe Priorität wenn eine Zuwanderungsgeschichte vorhanden ist',
      'High priority when immigration history is present',
    ] satisfies [string, string];
  }
}

@Entity()
export class IcsrmUserIsBornInCountryWaitinglistPriorityRule extends ProductWaitinglistPriorityRule {
  static override _class = 'IcsrmUserIsBornInCountryWaitinglistPriorityRule';

  override hasValue(): boolean {
    return true;
  }

  override renderEditUI(opt: { tr: TranslationController<BHTranslationDictionary> }) {
    return nothing as unknown as TemplateResult<1>;
  }

  override async hasHighPriority(userID: string): Promise<boolean> {
    const userStammdaten = await getStammdatenByUserAndInstitution<IcsrmStammdaten>(
      userID,
      this.institutionID,
    )({ nationality: 1 });

    if (!userStammdaten)
      throw new Error('could not find stammdaten for user=' + userID + ', institution=' + this.institutionID);

    return userStammdaten.nationality !== 'DE';
  }

  override async label() {
    return [
      'Hohe Priorität wenn die Nationalität nicht Deutsch ist',
      'High priority when nationality is not German',
    ] satisfies [string, string];
  }
}

export const icsrmRegistrationProductWaitinglistPriorityRulesPlugin: RegistrationProductWaitinglistPriorityRulesPlugin =
  {
    id: 'registration::product-waitinglist-priority-rules::icsrm',
    type: 'registration::product-waitinglist-priority-rules',
    getRuleKinds() {
      return [IcsrmImmigrationWaitinglistPriorityRule, IcsrmUserIsBornInCountryWaitinglistPriorityRule];
    },
    blockTimeForLowPriorityInDays: 0,
  };
